import { Role } from '@shared/models/enums';

/**
 * The pages in KMD Booking an organisation login's menu links to (Min forening / Min virksomhed,
 * Tilskud, Dokumenter, Min konto), relative to the tenant's KMD domain. Which of them a login gets
 * is decided by `MainMenuComponent` (permission and type) and, for whether the own pages exist at
 * all for the type, by `KmdMenuLinksService`.
 */

/**
 * The permissions (Indstillinger → Rettigheder, group Association) behind the own-association menu
 * items a foreningsbooker does not get: three groups of KMD pages and Min forening → Website tekst.
 * A KMD page without one is for every association login.
 */
export const OWN_MENU_PERMISSIONS = [
  'view_kmd_association_pages',
  'view_kmd_contributions',
  'view_kmd_association_inquiries',
  'view_association_website_text',
] as const;
export type OwnMenuPermission = (typeof OWN_MENU_PERMISSIONS)[number];

export interface KmdPage {
  title: string;
  path: string;
  permission?: OwnMenuPermission;
  /**
   * The organisation types (15, 4, 6) KMD has the page for; every type when left out. Held back until
   * the selected organisation's type is known, so it does not flash into another type's menu.
   */
  types?: readonly Role[];
}

const FOR_ASSOCIATIONS: readonly Role[] = [Role.Association];
const FOR_ASSOCIATIONS_AND_COMPANIES: readonly Role[] = [Role.Association, Role.Company];

/** A page under Min forening/Min virksomhed: all of them share the one permission. */
const ownPage = (title: string, path: string, types: readonly Role[]): KmdPage => ({
  title,
  path,
  types,
  permission: 'view_kmd_association_pages',
});

/**
 * Min forening / Min virksomhed → the organisation's own pages in KMD Booking, relative to the
 * tenant's KMD domain, in menu order. Titles, paths and types are what KMD's own menu has per type
 * (checked 2026-10-26): a company's Grundoplysninger is on its own path, its Historik, Brugere and
 * Dokumenter are on the association paths, a company has no Kontaktpersoner, and an internal customer
 * has no such group at all — so Min institution gets no KMD pages (`KmdMenuLinksService.ownHasPages`).
 */
export const KMD_OWN_PAGES: readonly KmdPage[] = [
  ownPage('Grundoplysninger', '/my-association/basic-information', FOR_ASSOCIATIONS),
  ownPage('Grundoplysninger', '/my-company/basic-information', [Role.Company]),
  ownPage('Kontaktpersoner', '/my-association/contactpersons', FOR_ASSOCIATIONS),
  ownPage('Historik', '/my-association/association-history', FOR_ASSOCIATIONS_AND_COMPANIES),
  ownPage('Brugere', '/my-company/sub-users', FOR_ASSOCIATIONS_AND_COMPANIES),
  ownPage('Dokumenter', '/my-association/documents', FOR_ASSOCIATIONS_AND_COMPANIES),
];

/** Tilskud → the organisation's grant pages in KMD Booking, relative to the tenant's KMD domain. */
const KMD_CONTRIBUTION_PAGES: readonly KmdPage[] = [
  { title: 'Tilskud og ansøgninger', path: '/contribution/contribution-inquiries', permission: 'view_kmd_contributions' },
  { title: 'Tilskudspuljer', path: '/contribution/contribution-inquiries/create', permission: 'view_kmd_contributions' },
];

/** Dokumenter → the grant documents in KMD Booking, relative to the tenant's KMD domain. Associations only. */
const KMD_DOCUMENT_PAGES: readonly KmdPage[] = [
  { title: 'Dokumenter og Informationer', path: '/files/guides', types: FOR_ASSOCIATIONS },
  {
    title: 'Tilskudsmodtageres regnskaber',
    path: '/files/financial',
    permission: 'view_kmd_contributions',
    types: FOR_ASSOCIATIONS,
  },
];

/** Min konto → the login's own user account in KMD Booking, relative to the tenant's KMD domain. */
const KMD_ACCOUNT_PAGES: readonly KmdPage[] = [
  { title: 'Grundoplysninger', path: '/useraccount' },
  { title: 'Notifikationer', path: '/useraccount/notifications' },
  {
    title: 'Foreningsansøgninger',
    path: '/useraccount/association-inquiries',
    permission: 'view_kmd_association_inquiries',
    types: FOR_ASSOCIATIONS,
  },
];

/** The organisation login's top-level KMD groups, after Min forening. A group left without pages is left out. */
export const KMD_GROUPS: readonly { title: string; icon: string; pages: readonly KmdPage[] }[] = [
  { title: 'Tilskud', icon: 'volunteer_activism', pages: KMD_CONTRIBUTION_PAGES },
  { title: 'Dokumenter', icon: 'description', pages: KMD_DOCUMENT_PAGES },
  { title: 'Min konto', icon: 'account_circle', pages: KMD_ACCOUNT_PAGES },
];
